import { useState, useEffect, useRef } from 'react';
import '../styles/SelectorFondo.css';

const PRESETS = [
  { nombre: 'Carbón',   valor: '#1c1c1e' },
  { nombre: 'Nogal',    valor: '#221a12' },
  { nombre: 'Concreto', valor: '#252525' },
  { nombre: 'Naval',    valor: '#151e2b' },
  { nombre: 'Óxido',    valor: '#1e1510' },
  { nombre: 'Pizarra',  valor: '#1a1f24' },
];

const STORAGE_KEY = 'lucsgood_bg';

function esImagen(valor) {
  return valor && (valor.startsWith('http') || valor.startsWith('data:') || valor.startsWith('/'));
}

function aplicarFondo(valor) {
  if (!valor) return;
  if (esImagen(valor)) {
    document.body.style.backgroundImage    = `url(${valor})`;
    document.body.style.backgroundSize     = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundAttachment = 'fixed';
    document.body.style.backgroundColor   = '#1c1c1e';
  } else {
    document.body.style.backgroundImage    = 'none';
    document.body.style.backgroundSize     = '';
    document.body.style.backgroundPosition = '';
    document.body.style.backgroundAttachment = '';
    document.body.style.backgroundColor   = valor;
  }
}

export function useFondo() {
  const [fondo, setFondoState] = useState(() => {
    // localStorage como caché para evitar parpadeo al cargar
    return localStorage.getItem(STORAGE_KEY) || '#1c1c1e';
  });

  const setFondo = (valor) => {
    setFondoState(valor);
    localStorage.setItem(STORAGE_KEY, valor);
    aplicarFondo(valor);
    // Persiste en la base de datos para que sea igual en cualquier máquina
    fetch('/api/config', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fondo: valor }),
    }).catch(() => {}); // silencioso si falla
  };

  useEffect(() => {
    // Aplica el caché local inmediatamente (sin parpadeo)
    aplicarFondo(localStorage.getItem(STORAGE_KEY) || '#1c1c1e');
    // Luego trae el valor real de la DB (sincroniza entre máquinas)
    fetch('/api/config')
      .then(r => r.json())
      .then(data => {
        const fondoDB = data.fondo;
        if (fondoDB) {
          localStorage.setItem(STORAGE_KEY, fondoDB);
          setFondoState(fondoDB);
          aplicarFondo(fondoDB);
        }
      })
      .catch(() => {});
  }, []); // solo al montar

  return [fondo, setFondo];
}

// ─────────────────────────────────────────────
const SelectorFondo = ({ fondo, setFondo }) => {
  const [abierto, setAbierto]       = useState(false);
  const [urlInput, setUrlInput]     = useState('');
  const [subiendo, setSubiendo]     = useState(false);
  const [errorImg, setErrorImg]     = useState('');
  const ref      = useRef(null);
  const fileRef  = useRef(null);

  const esImg = esImagen(fondo);

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const aplicarURL = () => {
    const url = urlInput.trim();
    if (!url) return;
    setFondo(url);
    setUrlInput('');
    setAbierto(false);
    setErrorImg('');
  };

  const subirImagen = async (e) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    setSubiendo(true);
    setErrorImg('');
    try {
      const form = new FormData();
      form.append('imagenes', archivo);
      const res  = await fetch('/api/productos/upload', { method: 'POST', body: form });
      const data = await res.json();
      if (!res.ok || !data.urls?.[0]) throw new Error(data.error || 'Error al subir');
      setFondo(data.urls[0]);
      setAbierto(false);
    } catch (err) {
      setErrorImg(err.message);
    } finally {
      setSubiendo(false);
      e.target.value = '';
    }
  };

  const quitarImagen = () => {
    setFondo('#1c1c1e');
    setAbierto(false);
  };

  return (
    <div className="sf-wrapper" ref={ref}>
      <button
        className="sf-trigger"
        onClick={() => setAbierto(a => !a)}
        title="Cambiar fondo"
      >
        {esImg
          ? <span className="sf-muestra sf-muestra-img" style={{ backgroundImage: `url(${fondo})` }} />
          : <span className="sf-muestra" style={{ background: fondo }} />
        }
        🎨
      </button>

      {abierto && (
        <div className="sf-panel">

          {/* Presets de color */}
          <p className="sf-titulo">Color de fondo</p>
          <div className="sf-presets">
            {PRESETS.map(p => (
              <button
                key={p.valor}
                className={`sf-preset${fondo === p.valor ? ' activo' : ''}`}
                style={{ background: p.valor }}
                onClick={() => { setFondo(p.valor); setAbierto(false); }}
                title={p.nombre}
              >
                {fondo === p.valor && <span className="sf-check">✓</span>}
              </button>
            ))}
          </div>

          <label className="sf-custom-label">Color personalizado</label>
          <div className="sf-custom-row">
            <input
              type="color"
              className="sf-color-input"
              value={esImg ? '#1c1c1e' : fondo}
              onChange={e => setFondo(e.target.value)}
            />
            <span className="sf-hex">{esImg ? '—' : fondo}</span>
          </div>

          {/* Separador */}
          <div className="sf-separator" />

          {/* Imagen de fondo */}
          <p className="sf-titulo">Imagen de fondo</p>

          {/* Preview imagen actual */}
          {esImg && (
            <div className="sf-img-preview-wrap">
              <img src={fondo} alt="Fondo actual" className="sf-img-preview" />
              <button className="sf-img-quitar" onClick={quitarImagen} title="Quitar imagen">✕</button>
            </div>
          )}

          {/* Subir archivo */}
          <button
            className="sf-btn-upload"
            onClick={() => fileRef.current?.click()}
            disabled={subiendo}
          >
            {subiendo ? '⏳ Subiendo...' : '📁 Subir imagen'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={subirImagen}
          />

          {/* URL manual */}
          <div className="sf-url-row">
            <input
              type="text"
              className="sf-url-input"
              placeholder="O pegar URL..."
              value={urlInput}
              onChange={e => setUrlInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && aplicarURL()}
            />
            <button className="sf-url-btn" onClick={aplicarURL} disabled={!urlInput.trim()}>
              ✓
            </button>
          </div>

          {errorImg && <p className="sf-error">{errorImg}</p>}

        </div>
      )}
    </div>
  );
};

export default SelectorFondo;
